import type { FC, ReactNode } from "react";
import { useState } from "react";
import { DotsVertical } from "@untitledui/icons";
import { AnimatePresence, motion } from "motion/react";
import { Button as AriaButton, DialogTrigger as AriaDialogTrigger, Popover as AriaPopover } from "react-aria-components";
import { Avatar } from "@/components/base/avatar/avatar";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { UntitledLogo } from "@/components/foundations/logo/untitledui-logo";
import { UntitledLogoMinimal } from "@/components/foundations/logo/untitledui-logo-minimal";
import { cx } from "@/utils/cx";
import { MobileNavigationHeader } from "../base-components/mobile-header";
import { NavAccountMenu } from "../base-components/nav-account-card";
import { NavButton } from "../base-components/nav-button";
import { NavItemBase } from "../base-components/nav-item";
import { NavList } from "../base-components/nav-list";
import type { NavItemType } from "../config";

interface SidebarNavigationSlimProps {
    /** URL of the currently active item. */
    activeUrl?: string;
    /** List of items to display. */
    items: (NavItemType & { icon: FC<{ className?: string }> })[];
    /** List of footer items to display. */
    footerItems?: (NavItemType & { icon: FC<{ className?: string }> })[];
    /** Whether to hide the border. */
    hideBorder?: boolean;
    /** Whether to hide the right side border. */
    hideRightBorder?: boolean;
    /** Logo to render in the collapsed rail. Defaults to UntitledLogoMinimal. */
    logo?: ReactNode;
    /** Logo to render in the mobile header. Defaults to UntitledLogo. */
    logoMobile?: ReactNode;
    /** Avatar to render in the bottom of the rail. Defaults to a placeholder photo. */
    avatar?: ReactNode;
    /** Display name of the signed-in user (shown in the expanded panel). */
    userName?: string;
    /** Email of the signed-in user (shown in the expanded panel). */
    userEmail?: string;
    /** Content of the popover that opens when the avatar is clicked. Defaults to NavAccountMenu. */
    accountMenu?: ReactNode;
    /** Render as edge-to-edge rail (no rounded corners, single right border) instead of a floating card. */
    flush?: boolean;
    /**
     * Expand the rail on hover to show full labels (replaces the dual-tier
     * secondary popout). Mutually exclusive with the secondary panel — when
     * true, hovering expands the main rail inline instead of opening a side
     * panel.
     */
    expandOnHover?: boolean;
}

export const SidebarNavigationSlim = ({
    activeUrl,
    items,
    footerItems = [],
    hideBorder,
    hideRightBorder,
    logo,
    logoMobile,
    avatar,
    userName,
    userEmail,
    accountMenu,
    flush,
    expandOnHover,
}: SidebarNavigationSlimProps) => {
    const activeItem = [...items, ...footerItems].find((item) => item.href === activeUrl || item.items?.some((subItem) => subItem.href === activeUrl));
    const [currentItem, setCurrentItem] = useState(activeItem || items[1]);
    const [isHovering, setIsHovering] = useState(false);

    // When expandOnHover is on, hovering expands the main rail instead of
    // opening the dual-tier secondary panel. The two modes don't coexist.
    const isExpanded = Boolean(expandOnHover && isHovering);
    const isSecondarySidebarVisible =
        !expandOnHover && isHovering && Boolean(currentItem.items?.length);

    const MAIN_SIDEBAR_WIDTH = 68;
    const EXPANDED_SIDEBAR_WIDTH = 240;
    const SECONDARY_SIDEBAR_WIDTH = 256;

    const mainSidebar = (
        <aside
            style={{
                width: isExpanded ? EXPANDED_SIDEBAR_WIDTH : MAIN_SIDEBAR_WIDTH,
            }}
            className={cx(
                "group flex h-full max-h-full max-w-full overflow-y-auto overflow-x-hidden transition-[width] duration-200 ease-out",
                !flush && "py-1 pl-1",
                isSecondarySidebarVisible && "bg-primary",
            )}
        >
            <div
                className={cx(
                    "flex w-full flex-col justify-between bg-primary pt-5 transition duration-300",
                    flush
                        ? "border-r border-secondary"
                        : cx(
                              "rounded-xl ring-1 ring-secondary ring-inset",
                              hideBorder && !isSecondarySidebarVisible && "ring-transparent",
                          ),
                )}
            >
                <div className={cx("flex px-3", isExpanded ? "justify-start pl-4" : "justify-center")}>
                    {logo ?? <UntitledLogoMinimal className="size-6" />}
                </div>

                <ul className={cx("mt-5 flex flex-col gap-0.5", isExpanded ? "px-2" : "px-3.5")}>
                    {items.map((item) => (
                        <li key={item.label}>
                            {isExpanded ? (
                                <NavItemBase
                                    type="link"
                                    href={item.href}
                                    icon={item.icon}
                                    badge={item.badge}
                                    current={activeUrl === item.href}
                                >
                                    {item.label}
                                </NavItemBase>
                            ) : (
                                <NavButton
                                    current={currentItem.href === item.href}
                                    href={item.href}
                                    label={item.label || ""}
                                    icon={item.icon}
                                    onClick={() => setCurrentItem(item)}
                                />
                            )}
                        </li>
                    ))}
                </ul>
                <div className={cx("mt-auto flex flex-col gap-3 py-4", isExpanded ? "px-2" : "items-center px-3")}>
                    {footerItems.length > 0 && (
                        <ul className={cx("flex flex-col gap-0.5", !isExpanded && "items-center")}>
                            {footerItems.map((item) => (
                                <li key={item.label}>
                                    {isExpanded ? (
                                        <NavItemBase
                                            type="link"
                                            href={item.href}
                                            icon={item.icon}
                                            badge={item.badge}
                                            current={activeUrl === item.href}
                                        >
                                            {item.label}
                                        </NavItemBase>
                                    ) : (
                                        <NavButton
                                            current={currentItem.href === item.href}
                                            label={item.label || ""}
                                            href={item.href}
                                            icon={item.icon}
                                            onClick={() => setCurrentItem(item)}
                                        />
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}

                    <AriaDialogTrigger>
                        <AriaButton
                            className={({ isPressed, isFocused }) =>
                                cx(
                                    "group relative inline-flex items-center gap-3 rounded-full",
                                    isExpanded && "w-full justify-start rounded-lg px-2 py-2 hover:bg-secondary_hover",
                                    (isPressed || isFocused) && "outline-2 outline-offset-2 outline-focus-ring",
                                )
                            }
                        >
                            {avatar ?? (
                                <Avatar
                                    border
                                    status="online"
                                    src="https://www.untitledui.com/images/avatars/olivia-rhye?fm=webp&q=80"
                                    size="md"
                                    alt="Olivia Rhye"
                                />
                            )}
                            {isExpanded && (
                                <span className="flex min-w-0 flex-col items-start text-left">
                                    <span className="truncate text-sm font-semibold text-primary">
                                        {userName ?? "Olivia Rhye"}
                                    </span>
                                    <span className="truncate text-sm text-tertiary">
                                        {userEmail ?? "olivia@untitledui.com"}
                                    </span>
                                </span>
                            )}
                        </AriaButton>
                        <AriaPopover
                            placement="right bottom"
                            offset={8}
                            crossOffset={6}
                            className={({ isEntering, isExiting }) =>
                                cx(
                                    "will-change-transform",
                                    isEntering &&
                                        "duration-300 ease-out animate-in fade-in placement-right:slide-in-from-left-2 placement-top:slide-in-from-bottom-2 placement-bottom:slide-in-from-top-2",
                                    isExiting &&
                                        "duration-150 ease-in animate-out fade-out placement-right:slide-out-to-left-2 placement-top:slide-out-to-bottom-2 placement-bottom:slide-out-to-top-2",
                                )
                            }
                        >
                            {accountMenu ?? <NavAccountMenu />}
                        </AriaPopover>
                    </AriaDialogTrigger>
                </div>
            </div>
        </aside>
    );

    const secondarySidebar = (
        <AnimatePresence initial={false}>
            {isSecondarySidebarVisible && (
                <motion.div
                    initial={{ width: 0, borderColor: "var(--color-border-secondary)" }}
                    animate={{ width: SECONDARY_SIDEBAR_WIDTH, borderColor: "var(--color-border-secondary)" }}
                    exit={{ width: 0, borderColor: "rgba(0,0,0,0)", transition: { borderColor: { type: "tween", delay: 0.05 } } }}
                    transition={{ type: "spring", damping: 26, stiffness: 220, bounce: 0 }}
                    className={cx(
                        "relative h-full overflow-x-hidden overflow-y-auto bg-primary",
                        !(hideBorder || hideRightBorder) && "box-content border-r-[1.5px]",
                    )}
                >
                    <div style={{ width: SECONDARY_SIDEBAR_WIDTH }} className="flex h-full flex-col px-4 pt-6">
                        <h3 className="text-sm font-semibold text-brand-secondary">{currentItem.label}</h3>
                        <ul className="py-2">
                            {currentItem.items?.map((item) => (
                                <li key={item.label} className="py-px">
                                    <NavItemBase current={activeUrl === item.href} href={item.href} icon={item.icon} badge={item.badge} type="link">
                                        {item.label}
                                    </NavItemBase>
                                </li>
                            ))}
                        </ul>
                        <div className="sticky bottom-0 mt-auto flex justify-between bg-primary pb-5">
                            <div>
                                <p className="text-sm font-semibold text-primary">{userName ?? "Olivia Rhye"}</p>
                                <p className="text-sm text-tertiary">{userEmail ?? "olivia@untitledui.com"}</p>
                            </div>
                            <div className="absolute -top-1 right-0">
                                <ButtonUtility size="xs" color="tertiary" tooltip="Log out" icon={DotsVertical} />
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return (
        <>
            {/* Desktop sidebar navigation */}
            <div
                className="z-50 hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex"
                onPointerEnter={() => setIsHovering(true)}
                onPointerLeave={() => setIsHovering(false)}
            >
                {mainSidebar}
                {secondarySidebar}
            </div>

            {/* Placeholder to take up physical space because the real sidebar has `fixed` position. */}
            <div
                style={{
                    paddingLeft: MAIN_SIDEBAR_WIDTH,
                }}
                className="invisible hidden lg:sticky lg:top-0 lg:bottom-0 lg:left-0 lg:block"
            />

            {/* Mobile header navigation */}
            <MobileNavigationHeader logo={logoMobile ?? logo}>
                <aside className="group flex h-full max-h-full w-full max-w-full flex-col justify-between overflow-y-auto bg-primary">
                    {/* Match the closed mobile chrome's geometry exactly
                      * (h-14 + items-center + pl-4) so a consumer's brand
                      * mark sits at the same screen position before and
                      * after the drawer slides in — no 4px wobble when
                      * a taller logo (e.g., Orion's `h-8`) is passed. */}
                    <div className="flex h-14 items-center px-4">
                        {logoMobile ?? logo ?? <UntitledLogo className="h-6" />}
                    </div>

                    <NavList items={items} />

                    <div className="mt-auto flex flex-col gap-3 p-4">
                        {footerItems.length > 0 && (
                            <ul className="flex flex-col gap-0.5">
                                {footerItems.map((item) => (
                                    <li key={item.label}>
                                        <NavItemBase
                                            type="link"
                                            href={item.href}
                                            icon={item.icon}
                                            badge={item.badge}
                                            current={activeUrl === item.href}
                                        >
                                            {item.label}
                                        </NavItemBase>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Account row — same avatar + popover pattern as the
                          * desktop rail's expanded state, so the consumer's
                          * `accountMenu` (e.g. orion's AccountMenu with
                          * light/dark toggle + sign out) renders identically
                          * on mobile. */}
                        <AriaDialogTrigger>
                            <AriaButton
                                className={({ isPressed, isFocused }) =>
                                    cx(
                                        "group relative inline-flex w-full items-center justify-start gap-3 rounded-lg px-2 py-2 hover:bg-secondary_hover",
                                        (isPressed || isFocused) && "outline-2 outline-offset-2 outline-focus-ring",
                                    )
                                }
                            >
                                {avatar ?? (
                                    <Avatar
                                        border
                                        status="online"
                                        src="https://www.untitledui.com/images/avatars/olivia-rhye?fm=webp&q=80"
                                        size="md"
                                        alt="Olivia Rhye"
                                    />
                                )}
                                <span className="flex min-w-0 flex-1 flex-col items-start overflow-hidden text-left">
                                    <span className="w-full truncate text-sm font-semibold text-primary">
                                        {userName ?? "Olivia Rhye"}
                                    </span>
                                    <span className="w-full truncate text-sm text-tertiary">
                                        {userEmail ?? "olivia@untitledui.com"}
                                    </span>
                                </span>
                            </AriaButton>
                            <AriaPopover
                                placement="top start"
                                offset={8}
                                className={({ isEntering, isExiting }) =>
                                    cx(
                                        "will-change-transform",
                                        isEntering && "duration-300 ease-out animate-in fade-in slide-in-from-bottom-2",
                                        isExiting && "duration-150 ease-in animate-out fade-out slide-out-to-bottom-2",
                                    )
                                }
                            >
                                {accountMenu ?? <NavAccountMenu />}
                            </AriaPopover>
                        </AriaDialogTrigger>
                    </div>
                </aside>
            </MobileNavigationHeader>
        </>
    );
};
